import React, {useEffect} from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import AuthorizedRoute from './AuthorizedRoute';
import BulkUserUploadResultScreen from '../components/bulk-user-upload-module/BulkUserUploadResultScreen';
import ClientDetails from '../components/pages/client-details/ClientDetails';
import UserManagement from '../components/pages/user-management/UserManagement';
import UserProfile from '../components/pages/UserProfile';
import AurBulkUserFileProcessing from '../components/bulk-user-upload-module/AurBulkUserFileProcessing';
import BulkUserUploadScreen from '../components/bulk-user-upload-module/BulkUserUploadScreen';
import BulkUserLandingPage from '../components/pages/BulkUserLandingPage';
import AddEditUser from '../components/pages/user-management/AddEditUser';
import Home from '../components/pages/Home';
import ClientManagement from '../components/pages/client-management/ClientManagement';
import WelcomeScreenMultiClient from '../components/pages/WelcomeScreenMultiClient';
import DemoRoutes from './DemoRoutes';
import WelcomeScreenSingleClient from '../components/pages/WelcomeScreenSingleClient';
import ConciergeReports from '../components/pages/concierge-reports/ConciergeReports';
import {Permissions, Resources} from '../utils/authorization';
import ProjectOverview from '../components/pages/reports/ProjectOverview';
import ViewInvoices from '../components/pages/invoices/ViewInvoices';
import AurPageError from '../components/common/aur-pageerror/aur-pageerror';
import {useAuthRoutes} from './AuthRoutes';
import {useDispatch, useSelector} from 'react-redux';
import AurPageNotFound from '../components/common/aur-page-not-found/aur-page-not-found';
import InvitePage from '../components/pages/InvitePage';
import {isAuthenticated, selectUser} from '../redux/slices/userSlice';
import {resetChangeTracker} from '../redux/slices/globalChangeTrackerSlice';
import WelcomeScreenNoClients from '../components/pages/WelcomeScreenNoClients';
import EcaHomePage from '../components/pages/EcaHomePage';
import IndexSearchPage from '../components/pages/index-search/IndexSearchPage';
import IndexManagementLanding from '../components/indexing/IndexManagement/IndexManagementLanding';
import StandardIndexOverView from '../components/indexing/StandardIndex/StandardIndex';
import NLPIndexOverView from '../components/indexing/NLPIndex/NLPIndex';
import CustomIndexOverView from '../components/indexing/CustomIndex/CustomIndex';
import navConstants from '../constants/navConstants';
import CreateSTR from '../components/pages/search-term-report/CreateSTR';
import CustomIndexStep1 from '../components/indexing/CustomIndex/CreateCustomIndex/CustomIndexStep1';
import FieldManagement from '../components/pages/field-management/FieldManagement';
import ViewSTR from '../components/pages/view-str/ViewSTR';
import ManageOverlays from '../components/ecapages/eca-overview/eca-overlay/ManageOverlays';
import CreateOverlay from '../components/ecapages/eca-overview/eca-overlay/CreateOverlay';
import TaggingAuditReport from '../components/pages/tagging-report/TaggingAuditReport';
import ViewSettings from '../components/indexing/CustomIndex/ViewSettings';
import SearchReport from '../components/pages/search-audit-report/SearchReport';
import NLPSearchPage from '../components/pages/nlp-search/NLPSearchPage';
import ExportManagementLanding from '../components/eca-export-management/eca-export-management-landing/ExportManagementLanding';
import CreateExportStep1 from '../components/eca-export-management/create-new-export/CreateExportStep1';
import ManageSavedSearch from '../components/pages/saved-search/ManageSavedSearch';
import ExportViewDetails from '../components/eca-export-management/eca-export-management-landing/ExportViewDetails';
import BudgetTrackerOverview from '../components/budget-tracker/BudgetTrackerOverview';
import ManageSavedSearchesTemplate from '../components/pages/saved-searches-template/ManageSavedSearchesTemplate';
// import Feedback from '../components/pages/feedback/Feedback';
import {useAnalytics} from '../hooks/useAnalytics';
import {RootState} from '../redux/store';
import {
  GA_BLACKLIST_URLS,
  GA_DATA_MODE_AUTO,
  GA_MEMBER_URLS,
  GA_NON_USER_ROUTES,
} from '../constants/analyticsConstants';
import {isCurrentUrlMatch} from '../utils/analyticsUtils';
import EvidenceSource from '../components/pages/evidence-source/EvidenceSource';
import UploadNewEvidence from '../components/pages/upload-evidence-source/upload-new-evidence/UploadNewEvidence';
import {UploadFileCatalyst} from '../components/pages/upload-evidence-source/upload-file-catalyst/UploadFileCatalyst';
import {SecurePage} from '../components/pages/SecurePage';
import {TrackGAEventParams} from '../types/analyticsTypes';
import ServiceAgreement from '../components/pages/project-creation/ServiceAgreement/ServiceAgreement';
import HotLeadsForm from '../components/pages/project-creation/HotLeadsForm/HotLeadsForm';
import CreateProject from '../components/pages/project-creation/CreateProject/CreateProject';
import HotLeadsFormSuccess from '../components/pages/project-creation/HotLeadsForm/HotLeadsFormSuccess';
import ProjectCreationProgressDetail from '../components/pages/project-creation/ProjectCreationProgressDetail/ProjectCreationProgressDetail';
import {IS_HOT_LEAD_FORM_VISIBLE} from '../constants/constants';
import PowerBiReportsDashboard from '../components/power-bi-reports/PowerBiReportsDashboard';

const AppRoutes: React.FC = () => {
  const dispatch = useDispatch();
  const isAuthenticatedUser = useSelector(isAuthenticated);
  const user = useSelector(selectUser);

  // clear change tracker
  dispatch(resetChangeTracker());

  return (
    <Router>
      <RouteWatcher />
      <Routes>
        {/* Authentication related */}
        {useAuthRoutes()}
        <Route element={<PrivateRoute isAuthenticated={isAuthenticatedUser} />}>
          {/* Miscellaneous */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route
            path="/welcome-multi-client"
            element={<WelcomeScreenMultiClient />}
          />
          <Route path="/eci-home-page" element={<EcaHomePage />} />
          <Route path="/users/exportdetails" element={<ExportViewDetails />} />
          <Route
            path="/clients/saved-searches-template"
            element={<ManageSavedSearchesTemplate />}
          />

          <Route
            path="/eci-home-page/manage-overlays"
            element={<ManageOverlays />}
          />
          <Route
            path="/eci-home-page/export-management"
            element={<ExportManagementLanding />}
          />
          <Route
            path="/eci-home-page/create-new-export"
            element={<CreateExportStep1 />}
          />
          <Route
            path="/welcome-single-client"
            element={
              <WelcomeScreenSingleClient userKind={user?.userKind ?? 0} />
            }
          />
          <Route
            path="/welcome-no-client"
            element={<WelcomeScreenNoClients />}
          />
          <Route
            path="/welcome-no-client"
            element={<WelcomeScreenNoClients />}
          />
          <Route path="/demo/*" element={<DemoRoutes />} />
          <Route path="/business-reports" element={ <PowerBiReportsDashboard />} />
          <Route path="/index-search" element={<IndexSearchPage />} />
          <Route path="/nlp-search" element={<NLPSearchPage />} />
          <Route path="/budgettracker" element={<BudgetTrackerOverview />} />
          {/* STR */}
          <Route path={navConstants.str.baseSTR} element={<CreateSTR />} />
          <Route path={navConstants.str.viewSTR} element={<ViewSTR />} />

          {/* Project Creation */}
          <Route
            path={navConstants.projectCreation.serviceAgreement}
            element={<ServiceAgreement />}
          />
          {IS_HOT_LEAD_FORM_VISIBLE && (
            <Route
              path={navConstants.projectCreation.hotLeadsForm}
              element={<HotLeadsForm />}
            />
          )}

          <Route
            path={navConstants.projectCreation.createProject}
            element={<CreateProject />}
          />
          <Route
            path={navConstants.projectCreation.hotLeadsFormSuccess}
            element={<HotLeadsFormSuccess />}
          />

          <Route
            path={navConstants.projectCreation.progressDetail}
            element={<ProjectCreationProgressDetail />}
          />

          {/* Users */}
          <Route
            element={
              <AuthorizedRoute
                permission={Permissions.View}
                resource={Resources.Users}
                userKind={user?.userKind ?? 0}
              />
            }
          >
            <Route path="/users" element={<UserManagement />} />
            <Route path="/users/add" element={<AddEditUser />} />
            <Route path="/users/bulkupload" element={<BulkUserLandingPage />} />
            <Route
              path="/users/bulkupload/fileprocessing"
              element={<AurBulkUserFileProcessing />}
            />
            <Route
              path="/users/bulkupload/add"
              element={<BulkUserUploadScreen />}
            />
            <Route
              path="/users/bulkupload/results"
              element={<BulkUserUploadResultScreen />}
            />
            <Route path="/users/details" element={<UserProfile />} />
          </Route>

          {/* Clients */}
          <Route
            element={
              <AuthorizedRoute
                permission={Permissions.View}
                resource={Resources.Clients}
                userKind={user?.userKind ?? 0}
              />
            }
          >
            <Route path="/clients" element={<ClientManagement />} />
          </Route>
          <Route path="/clients/details" element={<ClientDetails />} />
          {/* Reports */}
          <Route
            element={
              <AuthorizedRoute
                permission={Permissions.View}
                resource={Resources.Reports}
                userKind={user?.userKind ?? 0}
              />
            }
          >
            <Route path="/customreports" element={<ConciergeReports />} />
            <Route path="/manage-evidence" element={<EvidenceSource />} />
            <Route path="/reports" element={<ProjectOverview />} />
            <Route
              path="/indexmanagement"
              element={<IndexManagementLanding />}
            />
            <Route
              path="/eci-home-page/manage-overlays/createoverlay"
              element={<CreateOverlay />}
            />
            <Route
              path="/indexmanagement/standard-index"
              element={<StandardIndexOverView />}
            />
            <Route
              path="/indexmanagement/nlp-index"
              element={<NLPIndexOverView />}
            />
            <Route
              path="/indexmanagement/custom-index"
              element={<CustomIndexOverView />}
            />
            <Route
              path="/eci-home-page/tagging-report"
              element={<TaggingAuditReport />}
            />
            <Route
              path="/eci-home-page/search-report"
              element={<SearchReport />}
            />
            <Route
              path="/eci-home-page/saved-search"
              element={<ManageSavedSearch />}
            />
            <Route
              path="/indexmanagement/create-custom-index"
              element={<CustomIndexStep1 />}
            />
            <Route
              path="/upload-evidence/new-evidence"
              element={<UploadNewEvidence />}
            />
            <Route
              path="/upload-file-catalyst"
              element={<UploadFileCatalyst />}
            />
            <Route
              path="/indexmanagement/settings"
              element={<ViewSettings />}
            />
            <Route path="/reports/invoices" element={<ViewInvoices />} />
          </Route>

          <Route>
            <Route path="/field-management" element={<FieldManagement />} />
          </Route>
          {/* Unauthorized */}
          <Route path="/unauthorized" element={<AurPageError />} />
          {isAuthenticatedUser && (
            <Route path="*" element={<AurPageNotFound />} />
          )}

          {/* <Route>
            <Route path="/feedback" element={<Feedback />} />
          </Route> */}

          <Route path="/secure-page" element={<SecurePage />} />
        </Route>

        {/* Catch-all route */}
        <Route path="*" element={<AurPageNotFound />} />
        {/*user invite route */}
        <Route path="/invitation" element={<InvitePage />} />
      </Routes>
    </Router>
  );
};

const RouteWatcher = () => {
  const location = useLocation();
  const pathname = location.pathname + location.search;
  const {trackGAEvent} = useAnalytics();
  const {user} = useSelector((state: RootState) => state.analytics);
  const {analyticsInfo} = useSelector((state: RootState) => state.teamMember);

  useEffect(() => {
    const isBlackListedUrl = isCurrentUrlMatch(
      location.pathname,
      GA_BLACKLIST_URLS
    );
    if (isBlackListedUrl) {
      return;
    }
    const isNonUserRoute = GA_NON_USER_ROUTES.includes(location.pathname);
    const hasUser = Boolean(user?.id);
    const hasUserRole = Boolean(user?.role);
    const event: TrackGAEventParams = {};
    const isMemberUrl = isCurrentUrlMatch(location.pathname, GA_MEMBER_URLS);
    if (isMemberUrl) {
      event.member = {
        member_role: analyticsInfo.userRole,
        member_project_count: analyticsInfo.numberOfProject,
      };
    }
    if (isNonUserRoute && !hasUser) {
      trackGAEvent({page: GA_DATA_MODE_AUTO, ...event});
      return;
    }

    if (!isNonUserRoute && hasUser && hasUserRole) {
      trackGAEvent({
        page: GA_DATA_MODE_AUTO,
        user: GA_DATA_MODE_AUTO,
        ...event,
      });
    }
  }, [pathname, user]);

  return null;
};

export default AppRoutes;
